import { CSSProperties, useEffect, useState } from "react";
import TextInput from "../../../../Components/TextInput";
import useSearchParam from "../../../../Hooks/useSearchParam";
import Button from "../../../../Components/Button";
import { packetErrorHandler } from "../../../../Utils/requestUtils";
import FullScreenLoading from "../../../../Components/Loading/FullScreenLoading";
import {
  getPriceChart,
  getTimeChart,
  getVolatilityChart,
} from "../../../../Requests/optionDomain";
import { LineChart } from "../../../../Components/Charts/LineChart";
import { RefreshOutlined } from "@mui/icons-material";
import Modal from "../../../../Components/Modal/Modal";
import DynamicDropdown from "../../../../Components/Dropdown/DynamicDropdown";
import { getGroups } from "../../../../Requests/optionDomain";
import { getStepInstruments } from "../../../../Requests/stepRequests";
// Common Components
import { StaticDropdown } from "src/Components";
// Types
import {
  IOptionDomainChartsReport,
  IPairPositionChartSearchParams,
  IPairPositionReport,
} from "src/Interfaces/optionDomain";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";

export enum ChartType {
  DailyLock = 1,
  Delta,
  Gama,
}

enum PositionType {
  All = 0,
  CS = 1,
  PS = 2,
  LS = 3,
  SS = 4,
  CC = 5, //side1 =>  buy instrumnet side2=> sell call
  PP = 6, //side1 =>  buy instrumnet side2=> buy put
  RCC = 7,
  RPP = 8,
}

interface IChartsReports {
  volatility: IOptionDomainChartsReport[];
  price: IOptionDomainChartsReport[];
  time: IOptionDomainChartsReport[];
}

interface IProps {
  row: IPairPositionReport;
}

const ChartUI = ({ row }: IProps) => {
  const volatilitySearchParam = useSearchParam({
    initialSearchParam: {
      from: 0.05,
      to: 0.5,
      pointCount: 50,
      ...JSON.parse(
        localStorage.getItem("PairPositionVolatilityChartSearchParams") || "{}"
      ),
      ins1: row.instrumentId1,
      ins2: row.instrumentId2,
      positionType1: row.positionType1,
    } as IPairPositionChartSearchParams,
  });
  const priceSearchParam = useSearchParam({
    initialSearchParam: {
      from: -0.2,
      to: 0.2,
      pointCount: 50,
      ...JSON.parse(
        localStorage.getItem("PairPositionPriceChartSearchParams") || "{}"
      ),
      ins1: row.instrumentId1,
      ins2: row.instrumentId2,
      positionType1: row.positionType1,
    } as IPairPositionChartSearchParams,
  });

  const [chartReport, setChartReport] = useState({} as IChartsReports);
  const [loading, setLoading] = useState(false);
  // const [refreshFlag, setRefreshFlag] = useState(false);
  const [isSearchParamOpen, setIsSearchParamOpen] = useState(false);

  useEffect(() => {
    if (
      checkDisabled(volatilitySearchParam.serverSearchParams) ||
      checkDisabled(priceSearchParam.serverSearchParams)
    )
      return setIsSearchParamOpen(true);
    setIsSearchParamOpen(false);
    fetchCharts();
  }, [
    volatilitySearchParam.serverSearchParams,
    priceSearchParam.serverSearchParams,
  ]);

  // useEffect(() => {
  //   if (refreshFlag) {
  //     priceSearchParam.onSubmit();
  //     volatilitySearchParam.onSubmit();
  //     setRefreshFlag(false);
  //   }
  // }, [refreshFlag]);

  function fetchCharts() {
    setLoading(true);
    Promise.all([
      getVolatilityChart(volatilitySearchParam.serverSearchParams),
      getPriceChart(priceSearchParam.serverSearchParams),
      getTimeChart(priceSearchParam.serverSearchParams),
    ])
      .then((resp) => {
        setChartReport({
          price: resp[1].report,
          time: resp[2].report,
          volatility: resp[0].report,
        });
      })
      .catch((e) => {
        packetErrorHandler(e as any);
        setChartReport({
          price: [] as IOptionDomainChartsReport[],
          volatility: [] as IOptionDomainChartsReport[],
          time: [] as IOptionDomainChartsReport[],
        });
      })
      .finally(() => {
        setLoading(false);
        // setRefreshFlag(false);
      });
  }

  function checkDisabled(sp: IPairPositionChartSearchParams) {
    if (!sp.from || !sp.to || !sp.pointCount) return true;
    return false;
  }

  const getLabel = (chartType: ChartType) => {
    switch (chartType) {
      case ChartType.DailyLock:
        return "% بازده قفل روزانه";

      case ChartType.Delta:
        return "% دلتا";

      case ChartType.Gama:
        return "% گاما";

      default:
        return "";
    }
  };

  const getData = (
    report: IOptionDomainChartsReport[],
    chartType: ChartType
  ) => {
    switch (chartType) {
      case ChartType.DailyLock:
        return report.map((r) => r.dailyLockedProfitPortfolio);

      case ChartType.Delta:
        return report.map((r) => r.deltaPercent);

      case ChartType.Gama:
        return report.map((r) => r.gamaPercent);

      default:
        return report.map((r) => r.dailyLockedProfitPortfolio);
    }
  };

  const submitHandler = () => {
    volatilitySearchParam.onSubmit();
    priceSearchParam.onSubmit();
    localStorage.setItem(
      "PairPositionPriceChartSearchParams",
      JSON.stringify(priceSearchParam.clientSearchParams)
    );
    localStorage.setItem(
      "PairPositionVolatilityChartSearchParams",
      JSON.stringify(volatilitySearchParam.clientSearchParams)
    );
  };

  const bothSearchParamsChangeHandler = (name: string, value: any) => {
    volatilitySearchParam.clientSearchParamChangeHandler(name, value);
    priceSearchParam.clientSearchParamChangeHandler(name, value);
  };

  const searchParamModalUI = (
    <div className="flex flex-col justify-start items-start gap-3 w-full mt-10">
      <div className="flex flex-row justify-start items-end gap-3 w-full">
        <div className="w-64">
          <TextInput
            label="شروع نوسان پذیری"
            name="from"
            value={volatilitySearchParam.clientSearchParams.from?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="پایان نوسان پذیری"
            name="to"
            value={volatilitySearchParam.clientSearchParams.to?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="تعداد نقاط"
            name="pointCount"
            value={volatilitySearchParam.clientSearchParams.pointCount?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
      </div>
      <div className="flex flex-row justify-start items-end gap-3 w-full">
        <div className="w-64">
          <TextInput
            label="شروع قیمت"
            name="from"
            value={priceSearchParam.clientSearchParams.from?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="پایان قیمت"
            name="to"
            value={priceSearchParam.clientSearchParams.to?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="تعداد نقاط قیمت"
            name="pointCount"
            value={priceSearchParam.clientSearchParams.pointCount?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
      </div>
      <div className="w-[150px] mr-auto">
        <Button
          onClick={submitHandler}
          text="محاسبه"
          variant="contained"
          disabled={checkDisabled(
            volatilitySearchParam.clientSearchParams &&
              priceSearchParam.clientSearchParams
          )}
        />
      </div>
    </div>
  );

  const getChartName = (chartName: "price" | "volatility" | "time") => {
    switch (chartName) {
      case "price":
        return "حساسیت به قیمت";

      case "volatility":
        return "حساسیت به نوسان پذیری";

      case "time":
        return "حساسیت به زمان";

      default:
        return "";
    }
  };

  const positionTypeOptions: DropdownItem[] = [
    { id: "all", name: "همه" },
    { id: "CS", name: "CS" },
    { id: "LS", name: "LS" },
    { id: "PS", name: "PS" },
    { id: "SS", name: "SS" },
    { id: "CC", name: "CC" },
    { id: "PP", name: "PP" },
    { id: "RCC", name: "RCC" },
    { id: "RPP", name: "RCC" },
  ];

  const getCharts = (
    report: IOptionDomainChartsReport[],
    chartName: "price" | "volatility" | "time"
  ) => (
    <div className="flex flex-col min-h-0 min-w-0 flex-1 overflow-hidden">
      <div className="text-xs text-center w-full shrink-0 py-0.5">
        {getChartName(chartName)}
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.DailyLock),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),

                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.DailyLock),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.Delta),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.Delta),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.Gama),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.Gama),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
    </div>
  );

  const yAxisLabelStyle: CSSProperties = {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
  };

  return (
    <div className="w-full h-full min-h-0 min-w-0 overflow-hidden flex flex-col">
      {isSearchParamOpen ? (
        <Modal title="تنظیمات"
          UI={searchParamModalUI}
          onClose={() => setIsSearchParamOpen(false)}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-row items-end gap-2 shrink-0 w-full min-w-0 overflow-hidden pb-1">
        <div
          className="cursor-pointer shrink-0"
          onClick={
            checkDisabled(volatilitySearchParam.clientSearchParams) &&
            checkDisabled(priceSearchParam.clientSearchParams)
              ? () => {}
              : () => {
                  priceSearchParam.onSubmit();
                  volatilitySearchParam.onSubmit();
                }
          }
        >
          <RefreshOutlined />
        </div>
        <div className="min-w-0 flex-1 max-w-[200px]">
          <StaticDropdown
            dropdown={{
              placeholder: "نوع پوزیشن",
              name: "positionType1",
              extraLabel: true,
            }}
            options={positionTypeOptions}
            onChange={bothSearchParamsChangeHandler}
            value={volatilitySearchParam.clientSearchParams.positionType1}
          />
        </div>
        <div className="min-w-0 flex-1 max-w-[200px]">
          <DynamicDropdown
            dropdown={{
              placeholder: "نماد طرف اول",
              name: "ins1",
              extraLabel: true,
            }}
            value={volatilitySearchParam.clientSearchParams.ins1}
            onChange={bothSearchParamsChangeHandler}
            getData={() => getStepInstruments()}
          />
        </div>
        <div className="min-w-0 flex-1 max-w-[200px]">
          <DynamicDropdown
            dropdown={{
              placeholder: "نماد طرف دوم",
              name: "ins2",
              extraLabel: true,
            }}
            value={volatilitySearchParam.clientSearchParams.ins2}
            onChange={bothSearchParamsChangeHandler}
            getData={() => getStepInstruments()}
          />
        </div>

        <div className="w-32 shrink-0">
          <Button
            text="تنظیمات"
            onClick={() => setIsSearchParamOpen(true)}
            variant="contained"
          />
        </div>
      </div>
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex flex-row flex-1 min-h-0 min-w-0 overflow-hidden">
        <div className="flex flex-col justify-around items-center w-7 shrink-0 py-6">
          <div className="text-[10px] leading-none whitespace-nowrap" style={yAxisLabelStyle}>
            درصد بازده قفل روزانه
          </div>
          <div className="text-[10px] leading-none whitespace-nowrap" style={yAxisLabelStyle}>
            درصد دلتا
          </div>
          <div className="text-[10px] leading-none whitespace-nowrap" style={yAxisLabelStyle}>
            درصد گاما
          </div>
        </div>
        <div className="flex flex-row flex-1 min-h-0 min-w-0 overflow-hidden">
          {chartReport && chartReport.time && chartReport.time.length ? (
            getCharts(chartReport.time, "time")
          ) : (
            <></>
          )}
          {chartReport && chartReport.price && chartReport.price.length ? (
            getCharts(chartReport.price, "price")
          ) : (
            <></>
          )}
          {chartReport &&
          chartReport.volatility &&
          chartReport.volatility.length ? (
            getCharts(chartReport.volatility, "volatility")
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartUI;
